import { useEffect, useRef, useState } from 'react'
import { ReviewType } from '@skop-app/lib-types'

import { useCreateElement } from '@/hooks'
import { useUserContext } from '@/context'
import type { ReferenceOptionsSnapshot } from '@/services/reference/reference.type'
import type { Context } from '@/types/grid'
import { openModal } from '@/components/modals/modal.registry.events'

import type {
  EditorOption,
  EditorOptionNode,
  EditorProperty,
  EditorValue,
} from './types'
import {
  canCreateInlineValue,
  findExistingEditorOption,
  findMatchingReferenceOptions,
  flattenEditorOptions,
  mergeCommittedTokens,
  normalizeCreateLabel,
  sanitizeCreateLabel,
  toCommittedTokens,
} from './utils'
import {
  isOptionSelectableStatus,
  OPTION_ARCHIVED_STATUS,
  OPTION_RETURNED_STATUS,
} from '@/services/property/option.status'
import {
  extractGroupCandidates,
  findExistingEditorOptionInGroup,
  findGroupNodeById,
  hasGroupedEditorOptions,
} from './grouping'
import {
  syncCreatedOptionIntoGridContext,
  syncCreatedReferenceIntoGridContext,
} from './contextOptionSync'

type UseEditorOptionParams = {
  fieldKey: string
  property?: EditorProperty
  context: Context
  availableOptions: EditorOptionNode[]
  currentValue: EditorValue
  onValueChange: (values: EditorValue) => void
  stopEditing: () => void
  /** Called when a mandatory field (name, byUserID) is cleared; typically shows Toast and stops editing */
  onMandatoryFieldEmpty?: () => void
  createErrorMessage: string
  noGroupsMessage: string
  existingReferencesMessage?: string
  ambiguousNameMessage?: string
  onCreateCommitted?: () => void
}

type UseEditorOptionReturn = {
  handleChange: (event: EditorOption | EditorOption[] | null) => void
  handleCreate: (inputValue: string) => Promise<boolean>
  canCreate: boolean
  canUpdate: boolean
  isCreating: boolean
  isAwaitingGroupSelection: boolean
  createError: string | null
  clearCreateError: () => void
  refreshReferences: () => Promise<void>
  isRefreshing: boolean
}

/**
 * Hook that encapsulates option selection and creation logic
 * Separates business logic from UI rendering concerns
 */
export const useEditorOption = ({
  fieldKey,
  property,
  context,
  availableOptions,
  currentValue,
  onValueChange,
  stopEditing,
  onMandatoryFieldEmpty,
  createErrorMessage,
  noGroupsMessage,
  existingReferencesMessage = createErrorMessage,
  ambiguousNameMessage = createErrorMessage,
  onCreateCommitted,
}: UseEditorOptionParams): UseEditorOptionReturn => {
  const { isAdmin, isSkopAdmin } = useUserContext()
  const { createElement, resubmitOption } = useCreateElement()
  const isMountedRef = useRef(true)
  const activeRequestTokenRef = useRef<symbol | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isAwaitingGroupSelection, setIsAwaitingGroupSelection] =
    useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  const { authorization, ownership } = context
  const isLocked = property?.isLocked ?? false
  const isRef = property?.isRef ?? false
  const view = property?.view
  const isMulti = property?.isMulti ?? false
  const isReferencePicker = isRef && view !== 'references'

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      activeRequestTokenRef.current = null
    }
  }, [])

  const clearCreateError = () => {
    setCreateError(null)
  }

  const syncReferenceSnapshot = (fresh: ReferenceOptionsSnapshot) => {
    Reflect.set(context, 'refOptions', fresh.refOptions)
    Reflect.set(context, 'referencesById', fresh.referencesById)
    if (fresh.deletedOptionIdsByKey)
      Reflect.set(context, 'deletedOptionIdsByKey', fresh.deletedOptionIdsByKey)
    Reflect.set(context, 'propertyOptions', {
      ...context.propertyOptions,
      name: fresh.nameOptions,
    })
    Reflect.set(context, 'refDataByKey', {
      ...context.refDataByKey,
      name: fresh.nameRefData,
    })
  }

  const refreshReferences = async () => {
    if (!isReferencePicker || activeRequestTokenRef.current) return
    const token = Symbol('reference-refresh')
    activeRequestTokenRef.current = token
    setIsRefreshing(true)
    try {
      const fresh = await context.refreshReferenceOptions?.()
      if (!fresh) throw new Error('Reference scope is not ready')
      if (isMountedRef.current && activeRequestTokenRef.current === token)
        syncReferenceSnapshot(fresh)
    } catch (error) {
      console.error('SelectCreateEditor reference refresh failed', error)
      if (isMountedRef.current) setCreateError(createErrorMessage)
    } finally {
      if (activeRequestTokenRef.current === token) {
        activeRequestTokenRef.current = null
        if (isMountedRef.current) setIsRefreshing(false)
      }
    }
  }

  const runAfterCommit = (
    afterCommit?: (() => Promise<void> | void) | null,
  ) => {
    if (!afterCommit) return

    queueMicrotask(() => {
      void Promise.resolve(afterCommit()).catch((error) => {
        console.error('SelectCreateEditor post-commit sync failed', error)
      })
    })
  }

  // Compute permissions
  const canCreateRefs = authorization?.referencesAuth?.canCreate ?? false
  const canCreateInList =
    !isRef && property?.key
      ? context.propertyCanCreateInList?.[property.key]
      : undefined
  const canCreate =
    (isRef
      ? canCreateRefs
      : (authorization?.optionsAuth?.canCreate ?? false)) &&
    !isLocked &&
    canCreateInlineValue(property) &&
    (isRef || canCreateInList !== false)
  const canUpdate =
    (authorization?.optionsAuth?.canUpdate ?? false) && !isLocked

  const handleChange = (event: EditorOption | EditorOption[] | null) => {
    const isMandatoryField = fieldKey === 'name' || fieldKey === 'byUserID'
    if (!event && isMandatoryField) {
      onMandatoryFieldEmpty?.()
      stopEditing()
      return
    }

    clearCreateError()

    const eventArray = Array.isArray(event) ? event : event ? [event] : []
    const currentTokens = new Set(toCommittedTokens(currentValue, fieldKey))
    if (
      eventArray.some((option) => {
        const retainsArchivedValue =
          isMulti &&
          property?.type === 'LIST' &&
          !isReferencePicker &&
          option.status === OPTION_ARCHIVED_STATUS &&
          currentTokens.has(toCommittedTokens(option, fieldKey)[0])
        return (
          !retainsArchivedValue &&
          (option.isSelectable === false ||
            !isOptionSelectableStatus(option.status))
        )
      })
    )
      return
    const ids = toCommittedTokens(eventArray, fieldKey)
    onValueChange(ids)
    if (!isMulti) {
      stopEditing()
    }
  }

  const handleCreate = async (inputValue: string) => {
    if (!property || activeRequestTokenRef.current || !canCreate) return false

    const nextLabel = sanitizeCreateLabel(inputValue)
    if (!nextLabel) return false

    clearCreateError()

    const nameOptions = (context.propertyOptions?.[property.key] ??
      []) as EditorOptionNode[]
    const creationOptions = isReferencePicker ? nameOptions : availableOptions
    const canCreateInGroupedList = hasGroupedEditorOptions(creationOptions)

    if (
      isReferencePicker &&
      findMatchingReferenceOptions(availableOptions, nextLabel).length > 0
    ) {
      setCreateError(existingReferencesMessage)
      return false
    }

    const finalizeCommittedValue = (
      nextValue: EditorValue,
      afterCommit?: (() => Promise<void> | void) | null,
    ) => {
      onValueChange(nextValue)
      onCreateCommitted?.()
      // Inline creation is asynchronous and leaves CreatableSelect in "create"
      // mode; close the editor once the value is committed so AG Grid persists
      // the selection before blur/menu interactions can override it.
      stopEditing()
      runAfterCommit(afterCommit)
    }

    const commitOptionSelection = (
      nextOption: Pick<EditorOption, 'id' | 'value'>,
      afterCommit?: (() => Promise<void> | void) | null,
    ) => {
      finalizeCommittedValue(
        mergeCommittedTokens({
          currentValue,
          nextOption,
          fieldKey,
          isMulti,
        }),
        afterCommit,
      )
    }

    const runTrackedCreateRequest = async <T>(
      run: () => Promise<T>,
      errorLogMessage: string,
    ): Promise<T | null> => {
      const requestToken = Symbol('select-create-request')
      activeRequestTokenRef.current = requestToken
      if (isMountedRef.current) {
        setIsCreating(true)
      }

      try {
        const result = await run()

        if (activeRequestTokenRef.current !== requestToken) {
          return null
        }

        return result
      } catch (error) {
        console.error(errorLogMessage, error)
        if (
          activeRequestTokenRef.current === requestToken &&
          isMountedRef.current
        ) {
          setCreateError(createErrorMessage)
        }
        return null
      } finally {
        if (activeRequestTokenRef.current === requestToken) {
          activeRequestTokenRef.current = null
          if (isMountedRef.current) {
            setIsCreating(false)
          }
        }
      }
    }

    const getReusableInlineOption = (
      option: EditorOption | undefined,
    ): EditorOption | undefined =>
      option && isOptionSelectableStatus(option.status) ? option : undefined

    const syncCurrentGridOption = ({
      option,
      parentOptionId,
    }: {
      option: Pick<EditorOption, 'id' | 'value' | 'label' | 'status'>
      parentOptionId?: string
    }) => {
      if (!property?.key) return

      syncCreatedOptionIntoGridContext({
        context,
        fieldKey: property.key,
        option,
        parentOptionId,
      })
    }

    const reuseOrResubmitExistingOption = async ({
      option,
      parentOptionId,
    }: {
      option: EditorOption
      parentOptionId?: string
    }) => {
      if (option.status === OPTION_RETURNED_STATUS) {
        const resubmitted = await runTrackedCreateRequest(
          () =>
            resubmitOption({
              inputValue: nextLabel,
              option,
              property,
              ownership,
              byUserID: context.byUserID,
              parentOptionId,
            }),
          'SelectCreateEditor resubmit failed',
        )

        if (!resubmitted) return false

        syncCurrentGridOption({
          option: resubmitted,
          parentOptionId,
        })
        commitOptionSelection(resubmitted, resubmitted.afterCommit)
        return true
      }

      commitOptionSelection(option)
      return true
    }

    const finishCreate = async ({
      parentOptionId,
    }: {
      parentOptionId?: string
    }) => {
      const created = await runTrackedCreateRequest(async () => {
        let existingOption: EditorOption | undefined
        if (isReferencePicker) {
          const fresh = await context.refreshReferenceOptions?.()
          if (!fresh) throw new Error('Reference scope is not ready')
          syncReferenceSnapshot(fresh)
          if (
            findMatchingReferenceOptions(
              fresh.refOptions as EditorOptionNode[],
              nextLabel,
            ).length > 0
          ) {
            if (isMountedRef.current) setCreateError(existingReferencesMessage)
            return null
          }
          const freshNameOptions = fresh.nameOptions as EditorOptionNode[]
          if (!parentOptionId && hasGroupedEditorOptions(freshNameOptions))
            throw new Error('Name list now requires a parent group')
          const freshParent = parentOptionId
            ? findGroupNodeById(freshNameOptions, parentOptionId)
            : undefined
          if (parentOptionId && !freshParent)
            throw new Error('Name group is no longer available')
          const candidates = parentOptionId
            ? (freshParent?.options ?? [])
            : flattenEditorOptions(freshNameOptions)
          const matches = candidates.filter(
            (option) =>
              !(option as EditorOptionNode).options &&
              ['ACCEPTED', 'PENDING', 'RETURNED'].includes(
                option.status ?? '',
              ) &&
              option.isSelectable !== false &&
              normalizeCreateLabel(option.label) ===
                normalizeCreateLabel(nextLabel),
          )
          if (matches.length > 1) {
            if (isMountedRef.current) setCreateError(ambiguousNameMessage)
            return null
          }
          existingOption = matches[0]
        }
        return createElement({
          inputValue: nextLabel,
          property,
          field: fieldKey,
          view,
          ownership,
          isAdmin,
          isSkopAdmin,
          byUserID: context.byUserID,
          reviewType: isRef
            ? ReviewType.REVIEW_REFERENCE
            : ReviewType.REVIEW_OPTION,
          parentOptionId,
          ...(existingOption ? { existingOption } : {}),
        })
      }, 'SelectCreateEditor create failed')

      if (!created) return false

      if (created.target === 'reference' && created.createdReference) {
        const createdReferenceOptionId =
          created.createdReference.properties?.[fieldKey]?.[0]
        if (
          typeof createdReferenceOptionId === 'string' &&
          created.nameOption?.isNew
        ) {
          syncCurrentGridOption({
            option: {
              id: createdReferenceOptionId,
              value: createdReferenceOptionId,
              label: created.nameOption.label,
              status: created.nameOption.status,
            },
            parentOptionId,
          })
        }
        syncCreatedReferenceIntoGridContext({
          context,
          label: created.label,
          reference: created.createdReference,
        })

        const createdReferenceOption: EditorOption = {
          id: created.id,
          value: created.value,
          label: created.label,
          createdReference: created.createdReference,
        }

        finalizeCommittedValue(
          isMulti
            ? [
                ...toCommittedTokens(currentValue, fieldKey),
                createdReferenceOption,
              ]
            : [createdReferenceOption],
          created.afterCommit,
        )
      } else {
        syncCurrentGridOption({
          option: created,
          parentOptionId,
        })
        commitOptionSelection(created, created.afterCommit)
      }

      return true
    }

    if (canCreateInGroupedList) {
      const groupCandidates = extractGroupCandidates(creationOptions)

      if (groupCandidates.length === 0) {
        setCreateError(noGroupsMessage)
        return false
      }

      setIsAwaitingGroupSelection(true)
      const opened = openModal('selectOptionParentGroup', {
        label: nextLabel,
        propertyLabel: property.label,
        groups: groupCandidates,
        onCancel: () => {
          if (!isMountedRef.current) return
          setIsAwaitingGroupSelection(false)
        },
        onConfirm: async (groupId) => {
          if (isMountedRef.current) {
            setCreateError(null)
          }
          const existingInGroup = !isReferencePicker
            ? findExistingEditorOptionInGroup({
                options: creationOptions,
                inputValue: nextLabel,
                groupId,
              })
            : undefined
          const existingRevivableInGroup =
            getReusableInlineOption(existingInGroup)

          const succeeded = existingRevivableInGroup
            ? await reuseOrResubmitExistingOption({
                option: existingRevivableInGroup,
                parentOptionId: groupId,
              })
            : await finishCreate({ parentOptionId: groupId })

          if (succeeded && isMountedRef.current) {
            setIsAwaitingGroupSelection(false)
          }

          return succeeded
        },
      })

      if (!opened) {
        setIsAwaitingGroupSelection(false)
        setCreateError(createErrorMessage)
      }

      return false
    }

    const existingOption = !isReferencePicker
      ? findExistingEditorOption(availableOptions, nextLabel)
      : undefined
    const existingRevivableOption = getReusableInlineOption(existingOption)
    if (existingRevivableOption) {
      return reuseOrResubmitExistingOption({ option: existingRevivableOption })
    }

    return finishCreate({})
  }

  return {
    handleChange,
    handleCreate,
    canCreate,
    canUpdate,
    isCreating,
    isAwaitingGroupSelection,
    createError,
    clearCreateError,
    refreshReferences,
    isRefreshing,
  }
}
